import * as React from 'react'
import { cn } from '../../lib/utils'

export interface PanelFooterProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Left-side slot — e.g. row count / hint text */
	leading?: React.ReactNode
	/** Right-side slot — usually a <Pagination> */
	actions?: React.ReactNode
	/**
	 * Alignment of children when neither `leading` nor `actions` are provided.
	 * @default 'end'
	 */
	align?: 'start' | 'center' | 'end' | 'between'
}

/**
 * Bottom bar of a panel (Figma "PanelDown"). Typically holds a <Pagination> on the right.
 */
const PanelFooter = React.forwardRef<HTMLDivElement, PanelFooterProps>(
	({ className, leading, actions, align = 'end', children, ...props }, ref) => {
		const justify = {
			start:   'justify-start',
			center:  'justify-center',
			end:     'justify-end',
			between: 'justify-between',
		}[align]
		const hasSlots = leading || actions
		return (
			<div
				ref={ref}
				className={cn(
					'flex items-center gap-4 p-4 bg-(--color-background)',
					hasSlots ? 'justify-between' : justify,
					className
				)}
				{...props}
			>
				{hasSlots ? (
					<>
						<div className="flex items-center gap-2 min-w-0">{leading}</div>
						<div className="flex items-center gap-2 shrink-0">{actions ?? children}</div>
					</>
				) : (
					children
				)}
			</div>
		)
	}
)

PanelFooter.displayName = 'PanelFooter'
export { PanelFooter }
